import { create } from "zustand";
import { DateTime } from "luxon";
import { IParkingInfoState } from "types/stores.type";
import { subscribeWithSelector } from "zustand/middleware";
import { IParkingMachine } from "../types/parkingMachine.type";
import { SESSION_RESET_MINUTES } from "../constants";

const useParkingInfoStore = create<IParkingInfoState>()(
    subscribeWithSelector((set, get) => ({
        parkingZone: null,
        parkingMachine: null,
        priceForParking: null,
        originalPriceForParking: null,
        toDate: DateTime.now(),
        currOptions: [],
        currentSession: null,
        sessions: [],
        sectionCode: null,
        featureType: null,
        textRaLevels: undefined,

        setParkingZone: (parkingZone) => set({ parkingZone }),
        setParkingMachine: (parkingMachine: IParkingMachine) => set({ parkingMachine }),
        setPriceForParking: (priceForParking) => set({ priceForParking }),
        setOriginalPriceForParking: (originalPriceForParking) => set({ originalPriceForParking }),
        setToDate: (toDate) => set({ toDate }),
        setCurrOptions: (currOptions) => set({ currOptions }),
        setCurrentSession: (currentSession) => set({ currentSession }),
        setSessions: (sessions) => set({ sessions }),
        setSectionCode: (sectionCode) => set({ sectionCode }),
        setFeatureType: (featureType) => set({ featureType }),
        setTextRaLevels: (textRaLevels) => set({ textRaLevels }),

        checkAndSetCurrentSession: async (licencePlate: string) => {
            const { sessions, sectionCode, parkingZone } = get();
            const now = DateTime.now();

            let currentSession =
                sessions
                    .filter((session) => {
                        const ticket = session.ticket;
                        const matchesLicenseAndSection =
                            ticket.licensePlate === licencePlate && ticket.sectionCode && ticket.sectionCode === sectionCode;

                        if (!matchesLicenseAndSection) return false;

                        const isActive = session.isActive !== false;
                        if (!isActive) return false;

                        if (session.to) {
                            const sessionEnd = DateTime.fromISO(session.to);
                            const timeSinceEnd = now.diff(sessionEnd, "minutes").minutes;

                            return timeSinceEnd < SESSION_RESET_MINUTES;
                        }

                        return false;
                    })
                    .sort((a, b) => {
                        const aEnd = a.to ? DateTime.fromISO(a.to) : DateTime.fromMillis(0);
                        const bEnd = b.to ? DateTime.fromISO(b.to) : DateTime.fromMillis(0);
                        return bEnd.toMillis() - aEnd.toMillis();
                    })[0] || null;

            set({ currentSession: currentSession || null });
        },

        isLimitExceeded: () => {
            const { currOptions, currentSession } = get();

            if (!currentSession || currOptions.length === 0) {
                return false;
            }

            return currOptions[0]?.isDisabled === true;
        },

        hasValidTariff: () => {
            const { parkingZone, parkingMachine } = get();
            if (!parkingZone && !parkingMachine) return false;
            if (parkingMachine?.tariffInfo?.maxHour && parkingMachine?.tariffInfo?.maxHour > 0) return true;
            return parkingZone?.tariffInfo?.maxHour ? parkingZone.tariffInfo.maxHour > 0 : false;
        },

        getSessionForLicencePlate: (licencePlate) => {
            const { sessions, sectionCode, parkingZone } = get();

            return (
                sessions.find((session) => {
                    const ticket = session.ticket;
                    return ticket.licensePlate === licencePlate && ticket.sectionCode && ticket.sectionCode === sectionCode;
                }) || null
            );
        },
    }))
);

export default useParkingInfoStore;
